import { FeedStatePill } from "../design-system/feed-state.js";
import { formatDeliveryFormat } from "../lib/format.js";
import { useNow } from "../lib/use-now.js";
import { Artwork, LiveBars } from "./artwork.js";
import { BlockedInline } from "./blocked-notice.js";
import { usePlayer } from "./player-provider.js";
import { AiredProgress, PlayButton, VolumeControl } from "./transport.js";

/**
 * The desktop player is fixed to the bottom of the workspace, never across the
 * navigation rail and never scrolled away. Track on the left, transport in the
 * middle, identity and state on the right — the arrangement every music
 * application has taught people to read, given to radio by removing what radio
 * does not have: a seek bar, a queue, a previous and a next.
 */
export function PlayerBar() {
  const { detail, live, state, stop, target, toggle } = usePlayer();
  const airing = live.data?.nowPlaying ?? null;
  const now = useNow(1000, state === "playing" || state === "stalled");
  const delivery = live.data?.deliveries[0];

  return (
    <div className="fixed right-0 bottom-0 left-(--nav-w) z-40 hidden h-(--player-bar-h) border-t border-border bg-surface/95 backdrop-blur lg:block">
      <div className="mx-auto grid h-full max-w-[110rem] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-6 px-6">
        <div className="flex min-w-0 items-center gap-3.5">
          {target ? (
            <>
              <Artwork airing={airing} className="size-14" />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink">
                  {airing?.title ?? target.feedName}
                </div>
                <div className="mt-0.5 truncate text-xs text-ink-3">
                  {state === "blocked" ? (
                    <BlockedInline streamUrl={detail} />
                  ) : state === "error" ? (
                    <span className="text-err">{detail}</span>
                  ) : (
                    (airing?.artist ?? "") ||
                    (state === "connecting" ? "Connecting…" : target.feedName)
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3.5">
              <div className="grid size-14 place-items-center rounded-md border border-dashed border-border-hi bg-bg-2" />
              <div className="text-sm text-ink-3">
                Nothing playing. Pick a feed to listen.
              </div>
            </div>
          )}
        </div>

        <div className="flex w-[26rem] max-w-[34vw] flex-col items-center gap-2">
          <PlayButton
            disabled={!target}
            feedName={target?.feedName ?? "this feed"}
            onActivate={() => {
              if (!target) return;
              if (
                state === "idle" ||
                state === "error" ||
                state === "blocked"
              ) {
                toggle(target);
              } else stop();
            }}
            state={state}
          />
          <AiredProgress
            className="w-full"
            now={now}
            snapshot={live.data}
            withTimes
          />
        </div>

        <div className="flex min-w-0 items-center justify-end gap-5">
          {target ? (
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="min-w-0 text-right">
                <div className="flex items-center justify-end gap-2">
                  {state === "playing" ? (
                    <LiveBars className="text-accent-text" />
                  ) : null}
                  <span className="truncate text-xs font-medium text-ink-2">
                    {target.feedName}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-end gap-2">
                  <FeedStatePill state={live.data?.state ?? null} />
                  {delivery ? (
                    <span
                      className="hidden font-mono text-[0.6875rem] text-ink-3 xl:inline"
                      title={delivery.url}
                    >
                      {formatDeliveryFormat(delivery)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          <VolumeControl />
        </div>
      </div>
    </div>
  );
}
